/**
 * A subscription payment failed, and the clinic hears it here first (CLA-1574).
 *
 * Until this existed a declined card was invisible: the webhook set
 * `past_due`, nothing rendered it, and seven days later the plan paused and
 * the first anyone knew was a refused write. The server now also emails the
 * managers; this is the same fact where they actually spend the day.
 *
 * Decisions, alongside LapsedStrip's:
 *
 * - **Amber, not red, and not dismissible.** It is a deadline, not an outage —
 *   everything works — but hiding it would put them back to finding out by
 *   failure.
 * - **It names the date and the remedy.** "Working until Sunday" answers the
 *   question in their head (can I run tomorrow's list?); the link goes to
 *   Settings → Plan, where Manage billing and the unpaid invoice's Pay link are.
 * - **Role-aware.** Only someone who can manage the clinic gets the link.
 * - **Web words, server date.** The server's `past_due_notice` is written for
 *   the apps (no links, says where plans are managed). Web is allowed to link
 *   to the fix, so it writes its own line from `past_due_pause_at`.
 * - **Gives way to LapsedStrip.** Once writes are locked that strip is the
 *   truth; two banners about the same money would be noise.
 */
import { Link } from 'react-router'
import { CreditCard } from 'lucide-react'
import { useSubscription } from '@/api/subscription-hooks'
import { useAuthStore } from '@/stores/auth'
import { pauseDateLabel } from '@/lib/pause-date'

export function PastDueStrip() {
  const { data: sub } = useSubscription()
  const canManage = useAuthStore((s) => s.canManageClinic)()

  // Fails quiet, like LapsedStrip: never assert a state we cannot confirm.
  if (!sub?.past_due_pause_at || sub.writes_locked) return null

  const date = pauseDateLabel(sub.past_due_pause_at)

  return (
    <div className="shrink-0 bg-warning-light border-b border-warning/25">
      <div className="px-4 py-2 flex items-center gap-3 flex-wrap">
        <CreditCard className="w-4 h-4 text-warning shrink-0" aria-hidden />
        <p className="flex-1 min-w-[14rem] text-[13px] text-on-warning-soft">
          <span className="font-medium">The latest payment didn&rsquo;t go through.</span>{' '}
          Claro works as normal until {date}
          {canManage
            ? ' — update the card before then and nothing changes.'
            : ' — an owner or admin can update the card.'}
        </p>
        {canManage && (
          <Link
            to="/settings/plan"
            className="shrink-0 text-xs font-medium text-on-warning-soft hover:text-on-warning-soft underline underline-offset-2"
          >
            Update the card
          </Link>
        )}
      </div>
    </div>
  )
}
